import React, { useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, FileWarning, CheckCircle2 } from "lucide-react";

const SAMPLE = `client_name,account_name,symbol,trade_type,quantity,price,commission,trade_date,notes
Ravi Kumar,Default,AAPL,BUY,50,175.50,2.5,2026-01-15,Q1 accumulation
Ravi Kumar,Margin,MSFT,BUY,30,410.20,1.5,2026-01-16,
Priya Sharma,Default,TSLA,BUY,20,240.75,1.0,2026-01-17,New position`;

// Very small CSV parser (handles simple comma-separated w/ quoted fields)
function parseCSV(text) {
  const rows = [];
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length === 0) return { headers: [], data: [] };
  const parseLine = (l) => {
    const out = [];
    let cur = ""; let inQ = false;
    for (let i = 0; i < l.length; i++) {
      const ch = l[i];
      if (ch === '"' && l[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = !inQ;
      else if (ch === "," && !inQ) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map(s => s.trim());
  };
  const headers = parseLine(lines[0]).map(h => h.toLowerCase());
  for (let i = 1; i < lines.length; i++) {
    const cells = parseLine(lines[i]);
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = cells[idx] ?? ""; });
    rows.push(obj);
  }
  return { headers, data: rows };
}

export default function BulkImport({ onImported }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const t = await f.text();
    setText(t);
  };

  const submit = async () => {
    setResult(null);
    if (!text.trim()) return toast.error("Paste CSV or upload a file first");
    const { data } = parseCSV(text);
    if (data.length === 0) return toast.error("No data rows found");

    const rows = data.map(r => ({
      client_name: r.client_name || r["client name"] || "",
      client_id: r.client_id || "",
      account_name: r.account_name || r["account name"] || "",
      account_id: r.account_id || "",
      symbol: (r.symbol || "").toUpperCase(),
      stock_name: r.stock_name || r["stock name"] || "",
      trade_type: (r.trade_type || r.type || "").toUpperCase() === "SELL" ? "SELL" : "BUY",
      quantity: parseFloat(r.quantity || r.qty || 0),
      price: parseFloat(r.price || 0),
      commission: parseFloat(r.commission || 0),
      trade_date: r.trade_date || r.date || "",
      notes: r.notes || "",
    }));

    setBusy(true);
    try {
      const { data: resp } = await api.post("/transactions/bulk", { rows });
      setResult(resp);
      if (resp.created > 0) toast.success(`Imported ${resp.created} of ${resp.total_rows} trades`);
      if (resp.errors?.length) toast.warning(`${resp.errors.length} rows had issues — see report below`);
      if (resp.created > 0) { setText(""); onImported?.(); }
    } catch (e) {
      toast.error("Bulk import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-tactical p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-caps">Batch</div>
          <h2 className="font-heading font-bold text-xl mt-1">Bulk Trade Import</h2>
          <p className="text-xs text-slate-500 mt-1">CSV with headers: <span className="font-mono-tabular text-slate-400">client_name, account_name, symbol, trade_type, quantity, price, commission, trade_date, notes</span> <span className="text-slate-600">(account_name defaults to "Default")</span></p>
        </div>
        <label htmlFor="csv-file-input">
          <Button asChild variant="outline" className="border-slate-700 text-slate-800 cursor-pointer">
            <span><Upload size={14} className="mr-2" /> Upload CSV</span>
          </Button>
          <input id="csv-file-input" type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" data-testid="input-bulk-csv-file" />
        </label>
      </div>

      <Textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={SAMPLE}
        rows={7}
        data-testid="input-bulk-csv-text"
        className="bg-slate-900/70 border-slate-800 font-mono-tabular text-xs"
      />
      <div className="flex items-center justify-between mt-3">
        <button type="button" onClick={() => setText(SAMPLE)} data-testid="btn-fill-sample-csv" className="text-xs text-emerald-400 hover:underline">Fill sample</button>
        <Button onClick={submit} disabled={busy} data-testid="btn-bulk-import-submit" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">
          {busy ? "Importing..." : "Import Trades"}
        </Button>
      </div>

      {result && (
        <div className="mt-4 space-y-3" data-testid="bulk-import-report">
          <div className="flex items-center gap-2 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-sm">
            <CheckCircle2 size={16} /> Imported {result.created} of {result.total_rows} rows.
          </div>
          {result.errors?.length > 0 && (
            <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 text-sm">
              <div className="flex items-center gap-2 font-semibold mb-2"><FileWarning size={16} /> {result.errors.length} row(s) skipped</div>
              <ul className="space-y-1 text-xs font-mono-tabular max-h-48 overflow-y-auto">
                {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
